import _isArray from 'lodash/isArray';
import axios, { AxiosError } from 'axios';
import { defineStore } from 'pinia';
import { api } from 'services/api';
import { useUserStore } from '#src/store/user';
import useLogger from 'composables/logger';
import { useDeviceStore } from './device';

export const HISTORY_FAILURE_TAG = 'Failed to record history';

export const ACTION_TYPES = {
  IMPERSONATION: 'IMPERSONATION',
  MANUAL_ENTRY: 'MANUAL_ENTRY',
};

export type RecordableActionType = keyof typeof ACTION_TYPES;

export const useHistoryStore = defineStore({
  id: 'history',
  state: () => ({
    accountHistory: [] as HistoryItem[],
    accountHistoryError: null as string | null,
    currentDeviceHistory: [] as HistoryItem[],
    deviceHistoryError: null as string | null,
    isSavingNote: false,
    isLoadingAccountHistory: false,
    isLoadingHistory: false,
    isLoadingReturnHistory: false,
    returnHistory: [] as Array<HistoryItem & { isNew?: boolean }>,
  }),
  getters: {
    accountNotes(state) {
      return state.accountHistory.filter(({ comment }) => comment);
    },
    deviceNotes(state) {
      return state.currentDeviceHistory.filter(({ comment }) => comment);
    },
  },
  actions: {
    async addNote({
      accountKey,
      actionType,
      comment,
      details,
      forceType,
      historyId,
      historyIds,
      serial,
      userKey,
    }: {
      accountKey?: string;
      actionType?: RecordableActionType;
      comment: string;
      details?: string;
      forceType?: 'account' | 'device' | 'return' | 'user';
      historyId?: number;
      historyIds?: number[];
      serial?: string;
      userKey?: string;
    }) {
      this.isSavingNote = true;

      const result = {} as { error?: string; success?: boolean; history?: HistoryItem | HistoryItem[] };

      let historyType = null;
      let historyProp = '' as 'currentDeviceHistory' | 'accountHistory' | 'returnHistory';
      let parentEndpoint = '';
      let subPath = '';
      if (forceType) {
        switch (true) {
          case forceType === 'account' && !accountKey:
            throw new Error(`${HISTORY_FAILURE_TAG}: History type forced but no accountKey was given`);
          case forceType === 'device' && !serial:
            throw new Error(`${HISTORY_FAILURE_TAG}: History type forced but no serial was given`);
          case forceType === 'user' && !userKey:
            throw new Error(`${HISTORY_FAILURE_TAG}: History type forced but no userKey was given`);
          case forceType === 'return' && !historyId:
            throw new Error(`${HISTORY_FAILURE_TAG}: History type forced but no historyId was given`);
          default:
            historyType = forceType;
        }
      } else if (historyIds) {
        historyType = 'bulk';
      } else if (serial) {
        historyType = 'device';
      } else if (userKey) {
        historyType = 'user';
      } else if (accountKey) {
        historyType = 'account';
      }

      switch (historyType) {
        case 'account':
          historyProp = 'accountHistory';
          if (historyId) {
            parentEndpoint = 'history';
          } else {
            parentEndpoint = 'account';
            subPath = <string>accountKey;
          }
          break;
        case 'device':
          historyProp = 'currentDeviceHistory';
          if (historyId) {
            parentEndpoint = 'history';
          } else {
            parentEndpoint = 'device';
            subPath = <string>serial;
          }
          break;
        case 'user':
          historyProp = 'accountHistory';
          parentEndpoint = 'user';
          subPath = <string>userKey;
          break;
        case 'bulk':
          historyProp = 'currentDeviceHistory';
          parentEndpoint = 'device';
          subPath = 'bulk-config-update';
          break;
        case 'return':
          historyProp = 'returnHistory';
          parentEndpoint = 'history';
          break;
        default:
          if (historyId) {
            parentEndpoint = 'history';
            break;
          }
          throw new Error('Could not identify history type');
      }

      let historyPath = '';
      let method = 'post' as 'patch' | 'post';
      if (historyId) {
        historyPath = `/${historyId}`;
        method = 'patch';
      }

      const payload = {
        actionType,
        comment,
        details,
        history_id: historyId,
        history_ids: historyIds,
      } as {
        actionType?: RecordableActionType;
        accountKey?: string;
        comment: string;
        history_id?: number;
        history_ids?: number[];
      };

      if (userKey) {
        const { users } = useUserStore();
        const user = users[userKey];
        payload.accountKey = user.accountKey;
      }

      try {
        const { data } = await api[method]<HistoryResult | HistoryResult[]>(
          `/${parentEndpoint}${subPath ? `/${subPath}/comment` : ''}${historyPath}`,
          payload
        );

        const historyItemData = _isArray(data) ? data.map((result) => this.convertHistory(result)) : this.convertHistory(data);

        if (historyProp) {
          const updateHistory = (historyItem: HistoryItem) => {
            const itemIndex = this[historyProp].findIndex(({ id }) => id === historyItem.id);

            if (itemIndex > -1) {
              // Update current history item if it exists
              this.$patch((state) => {
                state[historyProp][itemIndex] = historyItem;
              });
            } else {
              // Prepend to history
              this.$state[historyProp].unshift(historyItem);
            }

            return historyItem;
          };

          // Update history in stores, if applicable
          (_isArray(historyItemData) ? historyItemData : [historyItemData]).forEach(updateHistory);
        }

        result.history = historyItemData;
        result.success = true;
      } catch (error) {
        const logger = useLogger();
        logger.axiosError('addNote error', <AxiosError>error);
        const errorMessage = (<AxiosError<{ error: string }>>error).response?.data?.error || (<Error>error).message;
        result.error = errorMessage;
      }

      this.isSavingNote = false;

      return result;
    },
    convertHistory(result: HistoryResult) {
      return {
        id: result.id,
        accountKey: result.account_key,
        deviceKey: result.device_key,
        agent: result.agent,
        agentHash: result.agent_hash,
        created: result.created,
        actionType: result.action_type,
        details: result.details,
        comment: result.comment,
      }
    },
    async getAccountHistory(key: string) {
      this.accountHistoryError = null;
      this.isLoadingAccountHistory = true;

      try {
        const { data } = await api.get<HistoryResult[]>(`/account/${key}/history`);
        this.accountHistory = data.map((result) => this.convertHistory(result));
      } catch (error) {
        if (axios.isAxiosError(error)) {
          this.accountHistoryError = (<ApiError['data']>error.response?.data)?.error || (<Error>error).message;
        } else {
          this.accountHistoryError = (<Error>error).message;
        }
      }

      this.isLoadingAccountHistory = false;
    },
    async getDeviceHistory(serial: string) {
      this.deviceHistoryError = null;
      this.isLoadingHistory = true;

      try {
        const { data } = await api.get<HistoryResult[]>(`/device/${serial}/history`);
        this.$state.currentDeviceHistory = data.map((result) => this.convertHistory(result));
      } catch (error) {
        if (axios.isAxiosError(error)) {
          this.$state.deviceHistoryError = (<ApiError['data']>error.response?.data)?.error || (<Error>error).message;
        } else {
          this.$state.deviceHistoryError = (<Error>error).message;
        }
      }

      this.isLoadingHistory = false;
    },
    async getHistoryById(historyId: number) {
      let result = { error: null } as HistoryItem | { error: string | null; };

      try {
        const { data } = await api.get<HistoryResult>(`/history/${historyId}`);
        result = this.convertHistory(data);
      } catch (error) {
        result = {
          error: (<AxiosError<{ error: string }>>error).response?.data?.error || (<Error>error).message,
        }
      }

      return result;
    },
    async getReturnHistory() {
      this.$state.isLoadingReturnHistory = true;

      try {
        const response = await api.get<HistoryResult[]>('/devices/returns');
        this.$state.returnHistory = response.data.map((result) => this.convertHistory(result));
      } catch (error) {
        const logger = useLogger();
        logger.error('getReturnHistory error:', <Error>error);
      }

      this.$state.isLoadingReturnHistory = false;

      return this.returnHistory;
    },
    async validateReturn(serial: string) {
      const deviceStore = useDeviceStore();
      const device = deviceStore.devices[serial];

      let result = {} as { isValid: boolean, returnData: HistoryResult, claimTimestamp: string | null; error?: string; };
      try {
        const { data } = await api.get<{ isValid: boolean; returnData: HistoryResult, claimTimestamp: string | null }>(`/devices/return/${<string>device.key}/validate`);
        result = data;
      } catch (error) {
        const logger = useLogger();
        logger.error('validateReturn error:', <Error>error);
        result.error = `Could not validate that device was not claimed since the last return: ${(<Error>error).message}`;
      }

      return result;
    },
    insertReturn(historyResult: HistoryResult) {
      this.$state.returnHistory.unshift(this.convertHistory(historyResult));
    },
  },
});
